import { useMemo, type FC } from "react";
import { Navigate, Outlet, useNavigate } from "react-router-dom";
import { Text } from "@/components/podkit/typography/Text";
import Breadcrumbs from "@/components/Breadcrumb";
import { webhookDetailsUrl, workflowsUrl } from "@/utils/automations";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { WebhookList } from "@/components/webhooks/WebhookList";
import { useFlagReusableWebhooksEnabled } from "@/hooks/use-flag";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { Button } from "@/components/flexkit/Button";
import { IconPlus } from "@/assets/icons/geist/IconPlus";
import { CreateWebhookDialog } from "@/components/webhooks/CreateWebhookDialog";
import { atom, useSetAtom, useAtomValue, type PrimitiveAtom } from "jotai";

export const WebhooksPage: FC = () => {
    useDocumentTitle("Webhooks");
    const { value: isReusableWebhooksEnabled, loading: isLoadingFlag } = useFlagReusableWebhooksEnabled();

    const isCreateWebhookDialogOpenAtom = useMemo(() => atom(false), []);
    const setIsCreateWebhookDialogOpen = useSetAtom(isCreateWebhookDialogOpenAtom);

    if (isLoadingFlag) {
        return <LoadingState />;
    }

    if (!isReusableWebhooksEnabled) {
        return <Navigate to={workflowsUrl()} replace />;
    }

    return (
        <div data-testid="webhooks-page" className="flex h-full grow flex-col gap-6">
            <div className="flex items-start justify-between">
                <div className="flex flex-col gap-1">
                    <Breadcrumbs
                        customBreadcrumbs={[{ label: "Automations", to: workflowsUrl() }, { label: "Webhooks" }]}
                    />
                    <Text className="text-content-base text-base">Manage webhooks for your automations.</Text>
                </div>
                <Button
                    variant="primary"
                    LeadingIcon={IconPlus}
                    onClick={() => setIsCreateWebhookDialogOpen(true)}
                    data-testid="add-webhook-button"
                    data-tracking-id="add-webhook-webhooks-page"
                >
                    Add Webhook
                </Button>
            </div>
            <WebhookList />
            <Outlet />
            <CreateWebhookDialogWrapper isCreateWebhookDialogOpenAtom={isCreateWebhookDialogOpenAtom} />
        </div>
    );
};

const CreateWebhookDialogWrapper: FC<{
    isCreateWebhookDialogOpenAtom: PrimitiveAtom<boolean>;
}> = ({ isCreateWebhookDialogOpenAtom }) => {
    const navigate = useNavigate();
    const isCreateWebhookDialogOpen = useAtomValue(isCreateWebhookDialogOpenAtom);
    const setIsCreateWebhookDialogOpen = useSetAtom(isCreateWebhookDialogOpenAtom);

    const handleClose = () => setIsCreateWebhookDialogOpen(false);

    const handleSuccess = (webhookId: string) => {
        setIsCreateWebhookDialogOpen(false);
        navigate(webhookDetailsUrl(webhookId), { replace: true });
    };

    return <CreateWebhookDialog open={isCreateWebhookDialogOpen} onClose={handleClose} onSuccess={handleSuccess} />;
};
